import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, IndexTable, Text } from "@shopify/polaris";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import agent from "../../app/api/agent";
import { formatLocalDateTime } from "./utils/dateTime";
import { AdminStoreSyncError, AdminSyncLogErrorsResponse } from "../../app/models/Admin";

type AdminConsoleContext = {
  token: string;
  onUnauthorized: () => void;
};


export default function AdminSyncLogErrorsPage() {
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const navigate = useNavigate();
  const { syncLogId } = useParams();
  const [syncLogErrorData, setSyncLogErrorData] = useState<AdminSyncLogErrorsResponse | null>(null);
  const [rows, setRows] = useState<AdminStoreSyncError[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedSyncLogId = Number(syncLogId);

  const title = useMemo(() => {
    const storeLabel = syncLogErrorData?.shopName || syncLogErrorData?.shopDomain || `Shop ${syncLogErrorData?.shopId ?? "-"}`;
    const domainSuffix = syncLogErrorData?.shopDomain ? ` (${syncLogErrorData.shopDomain})` : "";
    return `Sync log ${syncLogId} errors - ${storeLabel}${domainSuffix}`;
  }, [syncLogErrorData, syncLogId]);

  const loadErrors = useCallback(async () => {
    if (!Number.isFinite(parsedSyncLogId)) {
      setError("Invalid sync log id.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await agent.Admin.syncLogErrors(token, parsedSyncLogId);
      setSyncLogErrorData(response);
      setRows(response.errors || []);
    } catch {
      setError("Unable to load sync log errors.");
      onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [parsedSyncLogId, token, onUnauthorized]);

  useEffect(() => {
    loadErrors();
  }, [loadErrors]);

  return (
    <Card>
      <div style={{ display: "grid", gap: "0.75rem" }}>
        <div>
          <Button variant="plain" onClick={() => navigate("/pf-console/sync-logs")}>← Back to sync logs</Button>
        </div>
        <Text as="h3" variant="headingMd">{title}</Text>
        {error ? <Text as="p" tone="critical">{error}</Text> : null}
        {loading ? <Text as="p">Loading errors...</Text> : null}
        {!loading && rows.length === 0 ? <Text as="p">No errors found for this sync log.</Text> : null}
        {rows.length > 0 ? (
          <IndexTable
            resourceName={{ singular: "error", plural: "errors" }}
            itemCount={rows.length}
            selectable={false}
            headings={[{ title: "ID" }, { title: "Time" }, { title: "Error" }]}
          >
            {rows.map((row, index) => (
              <IndexTable.Row id={`${row.id}`} key={row.id} position={index}>
                <IndexTable.Cell>{row.id}</IndexTable.Cell>
                <IndexTable.Cell>{formatLocalDateTime(row.logTime)}</IndexTable.Cell>
                <IndexTable.Cell>
                  <Text as="span" tone="critical">{row.error || "(empty error)"}</Text>
                </IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
        ) : null}
      </div>
    </Card>
  );
}
